/**
 * S2.1 (TCH-01) — the copy of the teacher acquisition landing page.
 *
 * ADR-007 asks that this content stay "configurable independently from
 * application logic where practical": hero copy, video, testimonial, FAQ and
 * policy links are marketing assets that change on a marketing cadence, and
 * changing a sentence should not mean editing a screen.
 *
 * So the screen renders whatever is here and holds no copy of its own. There is
 * no CMS yet — when one arrives it replaces this module's export, not the
 * screen.
 *
 * The media URL comes from the environment rather than from this file: it
 * differs per stage and is the one field likely to be wrong. An unset value is
 * a supported state, not an error — see `TeacherLanding`.
 */

export interface ValueProp {
  emoji: string;
  title: string;
  body: string;
}

export interface AboutBlock {
  step: string;
  title: string;
  body: string;
}

export interface Testimonial {
  quote: string;
  name: string;
  detail: string;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export interface TeacherLandingContent {
  hero: {
    eyebrow: string;
    title: string;
    lead: string;
    ctaLabel: string;
    ctaLabelSignedIn: string;
    signInPrompt: string;
  };
  video: {
    /** Unset in every environment that has no film yet; the screen falls back. */
    url: string | null;
    title: string;
    caption: string;
    /** Shown in place of the player when there is no video, or it fails. */
    fallbackBody: string;
  };
  valueProps: ValueProp[];
  about: {
    title: string;
    blocks: AboutBlock[];
  };
  testimonial: Testimonial | null;
  faq: {
    title: string;
    entries: FaqEntry[];
  };
  closing: {
    title: string;
    body: string;
    ctaLabel: string;
  };
}

export const teacherLandingContent: TeacherLandingContent = {
  hero: {
    eyebrow: 'Para profesores',
    title: 'Enseña en IQMaxx',
    lead: 'Da clases en vivo a niñas y niños de 6 a 14 años. Tú defines tus materias, tu horario y tu tarifa; nosotros nos encargamos del cobro, la programación y el respaldo de cada sesión.',
    ctaLabel: 'Postúlate para enseñar',
    ctaLabelSignedIn: 'Continuar mi solicitud',
    signInPrompt: '¿Ya tienes cuenta?',
  },
  video: {
    url: process.env.REACT_APP_TEACHER_VIDEO_URL || null,
    title: 'Cómo funciona IQMaxx para profesores',
    caption: 'Tres minutos: de la solicitud a tu primera clase pagada.',
    fallbackBody:
      'El video de introducción aún no está disponible aquí. Lo que necesitas saber está descrito abajo, y puedes iniciar tu solicitud sin verlo.',
  },
  valueProps: [
    {
      emoji: '💸',
      title: 'Te pagamos por sesión cumplida',
      body: 'El pago del ciclo queda en garantía desde la inscripción y se libera sesión por sesión, cuando la impartes. No persigues facturas ni dependes de que la familia recuerde pagarte.',
    },
    {
      emoji: '🗓️',
      title: 'Tu horario, tus materias',
      body: 'Publicas los cursos que quieres dar y la disponibilidad que puedes sostener. Nadie te agenda fuera de ella.',
    },
    {
      emoji: '🎥',
      title: 'Aula y grabación incluidas',
      body: 'Cada sesión ocurre en una sala de video con grabación, así que la evidencia de tu trabajo queda registrada sin que tengas que hacer nada.',
    },
    {
      emoji: '🛡️',
      title: 'Un marco claro para trabajar',
      body: 'Verificamos a cada profesor y a cada familia, y las reglas de cancelación, ausencia y disputa están escritas antes de que las necesites.',
    },
  ],
  about: {
    title: 'Cómo es trabajar con nosotros',
    blocks: [
      {
        step: '1',
        title: 'Postúlate',
        body: 'Creas tu cuenta de profesor y completas tu solicitud: experiencia, materias, metodología y tus documentos de identidad y credencial.',
      },
      {
        step: '2',
        title: 'Te verificamos',
        body: 'Revisamos tu solicitud y tus documentos. Mientras tanto puedes preparar tus cursos en borrador; podrás publicarlos en cuanto quedes aprobado.',
      },
      {
        step: '3',
        title: 'Publicas tu curso',
        body: 'Defines el temario, el formato, el cupo, el precio y tu disponibilidad. Tu curso aparece en el catálogo con tu perfil público.',
      },
      {
        step: '4',
        title: 'Enseñas y cobras',
        body: 'Impartes las ocho sesiones del ciclo desde el aula de IQMaxx. Cada sesión cumplida libera su parte del pago, y tu panel muestra qué está liberado y qué sigue pendiente.',
      },
    ],
  },
  testimonial: {
    quote:
      'Llevaba años dando clases particulares y lo que más tiempo me quitaba no era enseñar: era cobrar, reagendar y explicar mis reglas una y otra vez. Aquí eso ya viene resuelto y yo preparo mis clases.',
    name: 'Profesora de matemáticas',
    detail: 'Primaria y secundaria · programa piloto IQMaxx',
  },
  faq: {
    title: 'Preguntas frecuentes',
    entries: [
      {
        question: '¿Cuánto tarda la verificación?',
        answer:
          'Depende del volumen de solicitudes y de que tus documentos estén completos y legibles. Te avisamos por correo cuando haya una decisión, y el estado de tu solicitud siempre está visible en tu panel.',
      },
      {
        question: '¿Qué necesito para postularme?',
        answer:
          'Una identificación oficial vigente y al menos una credencial o certificación que respalde lo que quieres enseñar. Puedes agregar cartas de referencia si las tienes.',
      },
      {
        question: '¿Cuándo recibo mi pago?',
        answer:
          'El pago del ciclo queda en garantía desde que la familia se inscribe y se libera por sesión, conforme las impartes y queda registrada la evidencia de la sesión. Una parte se retiene hasta el cierre del ciclo y se libera al terminarlo.',
      },
      {
        question: '¿Qué pasa si el alumno no llega?',
        answer:
          'Reportas la ausencia desde la sesión, después del periodo de tolerancia. La sesión cuenta como impartida para efectos de tu pago; no pierdes el tiempo que reservaste.',
      },
      {
        question: '¿Puedo dar clases si ya trabajo en una escuela?',
        answer:
          'Sí. Tú publicas únicamente la disponibilidad que puedes sostener y puedes cambiarla cuando cambie tu horario.',
      },
      {
        question: '¿Cuánto cobro por mis clases?',
        answer:
          'Tú fijas el precio de tu curso. La comisión de la plataforma se descuenta de cada liberación y la ves desglosada en tu panel de ingresos.',
      },
    ],
  },
  closing: {
    title: '¿Listo para dar tu primera clase?',
    body: 'Postularte toma unos minutos y puedes guardar tu solicitud a medias y volver después.',
    ctaLabel: 'Iniciar mi solicitud',
  },
};

export default teacherLandingContent;
